// apps/admin/src/components/command-palette.tsx
//
// P5 UI Polish W0 T6 — Cmd+K command palette skeleton (spec §1.6).
// P5 UI Polish W1 T3 — fills the palette with 11 commands (spec §2.2).
// P5 UI Polish W4 (deferred #10) — "Recent" section: the 5 most-recently
//   clicked commands persisted to localStorage under displaycms-cmdk-recent.
//
// W0 ships an EMPTY palette wired to the Cmd+K / Ctrl+K hotkey and
// Escape-to-close. W1 fills it with navigation + frequent operator
// actions ("Aller à Lecteurs", "Pause lecteur…", etc.).
//
// Built on cmdk (Radix-style headless primitive). The palette is a
// portal-rendered modal, focus-trapped, and dismissed by Escape, click-
// outside, or another Cmd+K toggle.

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Command } from 'cmdk';
import { useAuth } from '@/auth/auth-context';
import { Icon } from '@/components/icon';

const RECENT_STORAGE_KEY = 'displaycms-cmdk-recent';
const RECENT_MAX = 5;

function loadRecentIds(): string[] {
  try {
    const raw = window.localStorage.getItem(RECENT_STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((v): v is string => typeof v === 'string');
  } catch {
    return [];
  }
}

function persistRecentIds(ids: string[]): void {
  try {
    window.localStorage.setItem(RECENT_STORAGE_KEY, JSON.stringify(ids));
  } catch {
    // best-effort
  }
}

function addToRecent(ids: string[], newId: string): string[] {
  const deduped = ids.filter((id) => id !== newId);
  return [newId, ...deduped].slice(0, RECENT_MAX);
}

export interface CommandPaletteProps {
  /** Controlled open state. When provided, the component is fully controlled. */
  open?: boolean;
  /** Called when the palette should open or close (controlled mode). */
  onOpenChange?: (open: boolean) => void;
}

interface PaletteCommand {
  id: string;
  labelKey: string;
  to: string;
  roleReq?: ReadonlyArray<string>;
}

const COMMANDS: ReadonlyArray<PaletteCommand> = [
  { id: 'nav-dashboard', labelKey: 'nav:dashboard', to: '/' },
  { id: 'nav-resources', labelKey: 'nav:resources', to: '/resources' },
  { id: 'nav-composers', labelKey: 'nav:composers', to: '/composers' },
  { id: 'nav-players', labelKey: 'nav:players', to: '/players' },
  { id: 'nav-planning', labelKey: 'nav:planning', to: '/planning' },
  { id: 'nav-groups', labelKey: 'nav:groups', to: '/groups' },
  { id: 'nav-audit', labelKey: 'nav:audit', to: '/audit', roleReq: ['admin', 'auditor'] },
  { id: 'nav-users', labelKey: 'nav:users', to: '/users', roleReq: ['admin'] },
  { id: 'nav-settings', labelKey: 'nav:settings', to: '/settings', roleReq: ['admin'] },
  { id: 'create-resource', labelKey: 'common:command_palette.cmd_create_resource', to: '/resources/new' },
  { id: 'create-composer', labelKey: 'common:command_palette.cmd_create_composer', to: '/composers/new' },
];

export function CommandPalette({
  open: openProp,
  onOpenChange,
}: CommandPaletteProps = {}): JSX.Element {
  const { t } = useTranslation(['common', 'nav']);
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = openProp !== undefined;
  const open = isControlled ? openProp : internalOpen;

  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const userRoles: ReadonlyArray<string> = currentUser?.roles ?? [];

  // Recent commands: loaded from localStorage when the palette opens.
  const [recentIds, setRecentIds] = useState<string[]>(() => loadRecentIds());

  const setOpen = (next: boolean | ((prev: boolean) => boolean)): void => {
    const value = typeof next === 'function' ? next(open) : next;
    if (isControlled) {
      onOpenChange?.(value);
    } else {
      setInternalOpen(value);
    }
  };

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent): void => {
      // Cmd+K (Mac) OR Ctrl+K (other)
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((o) => !o);
        return;
      }
      // Escape closes
      if (e.key === 'Escape' && open) {
        e.preventDefault();
        setOpen(false);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open]);

  if (!open) return <></>;

  const visibleCommands = COMMANDS.filter(
    (c) => !c.roleReq || c.roleReq.some((r) => userRoles.includes(r)),
  );

  const goPrefix = t('common:command_palette.go_to_prefix', { defaultValue: 'Aller à ' });

  const handleSelect = (cmd: PaletteCommand): void => {
    // Update recent list: push to front, dedupe, trim to 5.
    const next = addToRecent(recentIds, cmd.id);
    setRecentIds(next);
    persistRecentIds(next);
    setOpen(false);
    navigate(cmd.to);
  };

  const isNavCommand = (id: string): boolean => id.startsWith('nav-');

  const getCommandLabel = (cmd: PaletteCommand): string =>
    isNavCommand(cmd.id) ? `${goPrefix}${t(cmd.labelKey)}` : t(cmd.labelKey);

  // The "Recent" section resolves IDs to visible commands in order.
  const recentCommands = recentIds
    .map((id) => visibleCommands.find((c) => c.id === id))
    .filter((c): c is PaletteCommand => c !== undefined);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('common:command_palette.aria_label', { defaultValue: 'Palette de commandes' })}
      data-testid="command-palette"
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 pt-[15vh]"
      onClick={() => { setOpen(false); }}
    >
      <Command
        className="w-full max-w-xl rounded-lg border border-border bg-card shadow-xl"
        onClick={(e) => e.stopPropagation()}
        loop
      >
        <Command.Input
          autoFocus
          placeholder={t('common:command_palette.placeholder', {
            defaultValue: 'Recherche rapide…',
          })}
          className="w-full border-b border-border bg-transparent px-4 py-3 text-sm outline-none placeholder:text-muted-foreground"
        />
        <Command.List className="max-h-[400px] overflow-auto p-2">
          <Command.Empty className="py-6 text-center text-sm text-muted-foreground">
            {t('common:command_palette.empty', {
              defaultValue: 'Aucune commande disponible.',
            })}
          </Command.Empty>
          {recentCommands.length > 0 && (
            <Command.Group
              heading={
                <span className="flex items-center gap-1 px-1 py-1 text-xs font-medium text-muted-foreground">
                  <Icon name="clock" size={12} />
                  {t('common:command_palette.recent_section_label', { defaultValue: 'Recent' })}
                </span>
              }
              data-testid="cmdk-recent-section"
            >
              {recentCommands.map((cmd) => {
                const label = getCommandLabel(cmd);
                return (
                  <Command.Item
                    key={`recent-${cmd.id}`}
                    value={`recent-${label}`}
                    onSelect={() => { handleSelect(cmd); }}
                    data-testid={`cmdk-recent-item-${cmd.id}`}
                    className="flex cursor-pointer items-center gap-2 rounded px-3 py-2 text-sm aria-selected:bg-muted"
                  >
                    <Icon name="clock" size={14} className="shrink-0 text-muted-foreground" />
                    {label}
                  </Command.Item>
                );
              })}
            </Command.Group>
          )}
          <Command.Group>
            {visibleCommands.map((cmd) => {
              const label = getCommandLabel(cmd);
              return (
                <Command.Item
                  key={cmd.id}
                  value={label}
                  onSelect={() => { handleSelect(cmd); }}
                  className="flex cursor-pointer items-center gap-2 rounded px-3 py-2 text-sm aria-selected:bg-muted"
                >
                  {label}
                </Command.Item>
              );
            })}
          </Command.Group>
        </Command.List>
      </Command>
    </div>
  );
}
